import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Grid, Paper, Typography, Box, alpha, Chip, Stack, Skeleton,
} from '@mui/material';
import {
  People, DirectionsCar, LocalShipping, HomeRepairService,
  AttachMoney, Map, Groups, PendingActions, TrendingUp,
} from '@mui/icons-material';
import StatCard from '../ui/StatCard';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import {
  aggregateJobStats,
  aggregateTodayActivity,
  formatActiveHint,
  formatServiceBreakdownHint,
} from '../../utils/jobStats';
import { formatPartnerHint, getPartnerBreakdown } from '../../utils/partnerStats';
import { formatActiveZonesValue, formatZoneHint } from '../../utils/zoneStats';
import { DASHBOARD_NAV } from '../../config/dashboardNavigation';

export interface DashboardOverview {
  totalUsers?: number;
  /** Transport driver profiles (fleet records). */
  totalDrivers?: number;
  totalPartners?: number;
  partnerDrivers?: number;
  partnerDeliveryPartners?: number;
  partnerMerchants?: number;
  partnerServiceProviders?: number;
  totalRides?: number;
  completedRides?: number;
  cancelledRides?: number;
  activeRides?: number;
  pendingRides?: number;
  otherRides?: number;
  totalPorterOrders?: number;
  completedPorterOrders?: number;
  cancelledPorterOrders?: number;
  activePorterOrders?: number;
  pendingPorterOrders?: number;
  totalOnDemandBookings?: number;
  completedOnDemandBookings?: number;
  cancelledOnDemandBookings?: number;
  activeOnDemandBookings?: number;
  pendingOnDemandBookings?: number;
  otherOnDemandBookings?: number;
  totalRevenue?: number;
  totalZones?: number;
  activeZones?: number;
  inactiveZones?: number;
  pendingSettlements?: number;
  pendingPartnerApprovals?: number;
  todayRides?: number;
  todayPorterOrders?: number;
  todayOnDemandBookings?: number;
  /** Backend-validated sum of all per-service active* counts. */
  activeJobs?: number;
  pendingJobs?: number;
  activeFoodOrders?: number;
  pendingFoodOrders?: number;
  activeGroceryOrders?: number;
  pendingGroceryOrders?: number;
}

interface DashboardStatsGridProps {
  overview?: DashboardOverview;
  loading?: boolean;
  enabledModules?: string[];
  isSuperAdmin?: boolean;
  role?: string;
  embedded?: boolean;
}

function isModuleEnabled(module: string, enabledModules: string[], isSuperAdmin?: boolean) {
  if (module === 'food' || module === 'grocery') return false;
  return isSuperAdmin || enabledModules.includes(module);
}

export default function DashboardStatsGrid({
  overview = {},
  loading,
  enabledModules = [],
  isSuperAdmin,
  role,
  embedded,
}: DashboardStatsGridProps) {
  const { formatAmount } = useTenantCurrency();
  const jobStats = useMemo(
    () => aggregateJobStats(overview, enabledModules, { isSuperAdmin, role }),
    [overview, enabledModules, isSuperAdmin, role],
  );

  const partnerBreakdown = useMemo(() => getPartnerBreakdown(overview), [overview]);

  const todayActivity = useMemo(
    () => aggregateTodayActivity(overview, enabledModules, { isSuperAdmin }),
    [overview, enabledModules, isSuperAdmin],
  );

  const primaryStats = [
    { title: 'Customers', value: overview.totalUsers ?? 0, icon: <People />, color: '#2563eb', hint: 'Registered customer accounts', to: DASHBOARD_NAV.customers },
    {
      title: 'Partners',
      value: partnerBreakdown.totalPartners,
      icon: <Groups />,
      color: '#7c3aed',
      hint: formatPartnerHint(overview),
      to: DASHBOARD_NAV.partners,
    },
    { title: 'Total Revenue', value: formatAmount(overview.totalRevenue ?? 0), icon: <AttachMoney />, color: '#059669', hint: 'Transport, porter & services', to: DASHBOARD_NAV.revenue },
    {
      title: 'Active Zones',
      value: formatActiveZonesValue(overview),
      icon: <Map />,
      color: '#0284c7',
      hint: formatZoneHint(overview),
      to: DASHBOARD_NAV.zones,
    },
  ];

  const moduleStats = [
    isModuleEnabled('transportation', enabledModules, isSuperAdmin) && {
      title: 'Transport Rides', value: overview.totalRides ?? 0, icon: <DirectionsCar />, color: '#2563eb',
      hint: formatServiceBreakdownHint({
        total: overview.totalRides ?? 0,
        completed: overview.completedRides ?? 0,
        pending: overview.pendingRides ?? 0,
        active: overview.activeRides ?? 0,
        cancelled: overview.cancelledRides ?? 0,
        other: overview.otherRides,
      }),
      to: DASHBOARD_NAV.transportRides,
    },
    isModuleEnabled('porter', enabledModules, isSuperAdmin) && {
      title: 'Porter Orders', value: overview.totalPorterOrders ?? 0, icon: <LocalShipping />, color: '#0f766e',
      hint: formatServiceBreakdownHint({
        total: overview.totalPorterOrders ?? 0,
        completed: overview.completedPorterOrders ?? 0,
        pending: overview.pendingPorterOrders ?? 0,
        active: overview.activePorterOrders ?? 0,
        cancelled: overview.cancelledPorterOrders ?? 0,
      }),
      to: DASHBOARD_NAV.porterOrders,
    },
    isModuleEnabled('on_demand', enabledModules, isSuperAdmin) && {
      title: 'Services', value: overview.totalOnDemandBookings ?? 0, icon: <HomeRepairService />, color: '#0891b2',
      hint: formatServiceBreakdownHint({
        total: overview.totalOnDemandBookings ?? 0,
        completed: overview.completedOnDemandBookings ?? 0,
        pending: overview.pendingOnDemandBookings ?? 0,
        active: overview.activeOnDemandBookings ?? 0,
        cancelled: overview.cancelledOnDemandBookings ?? 0,
        other: overview.otherOnDemandBookings,
        activeLabel: 'in progress',
      }),
      to: DASHBOARD_NAV.onDemandServices,
    },
  ].filter(Boolean) as { title: string; value: string | number; icon: React.ReactNode; color: string; hint: string; to: string }[];

  const opsStats = [
    {
      title: 'Needs Action',
      value: jobStats.pendingJobs,
      icon: <PendingActions />, color: '#dc2626',
      hint: 'Unassigned / pending jobs',
      to: DASHBOARD_NAV.needsAction,
    },
    {
      title: 'Active Jobs',
      value: jobStats.activeJobs,
      icon: <TrendingUp />, color: '#0891b2',
      hint: formatActiveHint(jobStats.activeJobs, jobStats.activeByModule),
      to: DASHBOARD_NAV.activeJobs,
    },
    {
      title: 'Pending Settlements',
      value: overview.pendingSettlements ?? 0,
      icon: <AttachMoney />, color: '#ca8a04',
      hint: 'Partner payouts due',
      to: DASHBOARD_NAV.pendingSettlements,
    },
    {
      title: 'Partner Approvals',
      value: overview.pendingPartnerApprovals ?? 0,
      icon: <Groups />, color: '#9333ea',
      hint: 'Awaiting verification',
      to: DASHBOARD_NAV.partnerApprovals,
    },
  ];

  if (loading) {
    return (
      <Grid container spacing={2} mb={3}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Grid item xs={6} md={3} key={i}><Skeleton variant="rounded" height={110} sx={{ borderRadius: 3 }} /></Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Box mb={embedded ? 0 : 3}>
      {!embedded && (
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Platform overview</Typography>
      )}
      <Grid container spacing={2} mb={2.5}>
        {primaryStats.map((stat) => (
          <Grid item xs={6} md={3} key={stat.title}>
            <StatCard title={stat.title} value={stat.value} icon={stat.icon} color={stat.color} hint={stat.hint} to={stat.to} />
          </Grid>
        ))}
      </Grid>

      {!!moduleStats.length && (
        <>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>By service</Typography>
          <Grid container spacing={2} mb={2.5}>
            {moduleStats.map((stat) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={stat.title}>
                <Paper
                  elevation={0}
                  component={RouterLink}
                  to={stat.to}
                  sx={{
                    p: 2,
                    height: '100%',
                    borderRadius: 3,
                    border: 1,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'block',
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                    background: `linear-gradient(135deg, ${alpha(stat.color, 0.08)}, transparent)`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha(stat.color, 0.16)}`,
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${alpha(stat.color, 0.55)}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                    <Box sx={{ color: stat.color, display: 'flex' }}>{stat.icon}</Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>{stat.title}</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={800} sx={{ color: stat.color }}>{stat.value}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>{stat.hint}</Typography>
                </Paper>
              </Grid>
            ))}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Paper
                elevation={0}
                component={RouterLink}
                to={DASHBOARD_NAV.todayActivity}
                sx={{
                  p: 2,
                  height: '100%',
                  borderRadius: 3,
                  border: 1,
                  borderColor: 'divider',
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'block',
                  cursor: 'pointer',
                  background: `linear-gradient(135deg, ${alpha('#0d9488', 0.08)}, transparent)`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha('#0d9488', 0.16)}`,
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                  <Box sx={{ color: '#0d9488', display: 'flex' }}><TrendingUp fontSize="small" /></Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Today&apos;s activity</Typography>
                </Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: '#0d9488' }}>{todayActivity.total}</Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.75} useFlexGap>
                  {todayActivity.byModule.map((row) => (
                    <Chip
                      key={row.module}
                      size="small"
                      label={`${row.count} ${row.label}`}
                      sx={{ height: 20, fontSize: '0.62rem' }}
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                  {todayActivity.total > 0 ? 'New or updated today' : 'No jobs created or updated today'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Operations</Typography>
      <Grid container spacing={2}>
        {opsStats.map((stat) => (
          <Grid item xs={6} md={3} key={stat.title}>
            <StatCard title={stat.title} value={stat.value} icon={stat.icon} color={stat.color} hint={stat.hint} to={stat.to} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
